/**
 * Reconciling a notes textarea against a server value that can change underneath it.
 *
 * Background: the tab holds `ppr_jobs.notes` in `useState` and used to initialise
 * it once, which assumed the server value could not move while it was mounted.
 * It can — the same job's notes may be saved from another session or another tab
 * of this one, and the refetched value then lands under a live textarea. (This
 * was first hit when cancelling a review appended its reason to the same column;
 * cancellation has its own columns now, but concurrent notes edits remain.)
 *
 * Pure and side-effect free so it can be unit-tested — the two failure modes here
 * are both silent, and both were shipped before being caught:
 *
 *  1. Not adopting a server change let the stale textarea read as an unsaved edit,
 *     so Save PATCHed the pre-refetch text back over whatever had arrived.
 *  2. Over-eagerly treating any change as a conflict flagged the component's OWN
 *     save — after saving, the refetched value differs from the previously-seen
 *     server value, which looks identical to someone else having edited it.
 */

export type NotesReconcile =
  /** Server value unchanged — nothing to do. */
  | { action: 'none' }
  /** Local text already equals the new server value (typically our own save). */
  | { action: 'in-sync' }
  /** No local edits were pending, so take the server's version. */
  | { action: 'adopt'; value: string }
  /** Local edits AND a server change that isn't ours — the user must decide. */
  | { action: 'conflict' };

/**
 * @param prevServer the server value this component last saw
 * @param nextServer the server value that just arrived
 * @param local      what is currently in the textarea
 */
export function reconcileNotes(
  prevServer: string,
  nextServer: string,
  local: string,
): NotesReconcile {
  if (prevServer === nextServer) return { action: 'none' };
  // Checked BEFORE the edits test: after an ordinary save the local text and the
  // refetched server value agree, and that must not read as someone else's edit.
  //
  // ⚠️ COMPARED TRIMMED. The tab saves `notes.trim()`, so a value the user typed
  // with leading or trailing whitespace comes back normalized and differs from
  // BOTH the previous server value and the still-untrimmed textarea — falling
  // through to `conflict` and leaving a false warning after a perfectly ordinary
  // save. Only this arm is normalized: `adopt` compares raw, because there the
  // question is whether the user typed anything at all, and whitespace they
  // typed is still an edit.
  if (local.trim() === nextServer.trim()) return { action: 'in-sync' };
  if (local === prevServer) return { action: 'adopt', value: nextServer };
  return { action: 'conflict' };
}
